import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import EditShortcutFolderPage from "@/components/edit-shortcut-folder-page";
import EditShortcutIconPage from "@/components/edit-shortcut-icon-page";
import styles from "@/components/edit-shortcut-popup.module.css";
import MenuShell from "@/components/menu-shell";
import PopupCloseButton from "@/components/popup-close-button";
import useCustomInput from "@/hooks/use-custom-input";
import * as signals from "@/signals";
import { EditShortcutPopupPage, FolderInfo, IconOption } from "@/types";
import { getColorUrl } from "@/utils";
import {
	faAngleRight,
	faFolderClosed,
	faHeading,
	faIcons,
	faLink,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSignalEffect } from "@preact/signals";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { FocusEvent, FormEvent, JSX, useId, useState } from "react";

function EditShortcutPopup(): JSX.Element {
	const queryClient = useQueryClient();

	const titleInputId = useId();
	const urlInputId = useId();

	const [currentPage, setCurrentPage] = useState<EditShortcutPopupPage>(
		EditShortcutPopupPage.MAIN,
	);
	const [focusedInputId, setFocusedInputId] = useState<string | null>(null);
	const [isClosing, setIsClosing] = useState<boolean>(false);
	const [selectedFolder, setSelectedFolder] = useState<FolderInfo | null>(
		signals.currentFolder.value,
	);
	const [selectedIconOption, setSelectedIconOption] = useState<IconOption>(
		IconOption.UNKNOWN,
	);

	let calculatedIconOption: IconOption;
	if (selectedIconOption) {
		calculatedIconOption = selectedIconOption;
	} else if (!signals.editingShortcut.value.icon) {
		calculatedIconOption = IconOption.AUTO;
	} else if (signals.editingShortcut.value.icon.startsWith("color://")) {
		calculatedIconOption = IconOption.LIME_GENERATED;
	} else {
		calculatedIconOption = IconOption.URL;
	}

	const titleInput = useCustomInput<HTMLInputElement>({
		setValue: (newValue) => {
			signals.editingShortcut.value = {
				...signals.editingShortcut.value,
				title: newValue,
			};
		},
	});
	const urlInput = useCustomInput<HTMLInputElement>({
		setValue: (newValue) => {
			signals.editingShortcut.value = {
				...signals.editingShortcut.value,
				url: newValue,
			};
		},
	});

	const handleClose = (): void => {
		setIsClosing(true);
		window.setTimeout(() => {
			setCurrentPage(EditShortcutPopupPage.MAIN);
			setSelectedFolder(signals.currentFolder.value);
			setSelectedIconOption(IconOption.UNKNOWN);
			signals.editShortcutPopup.value = {
				...signals.editShortcutPopup.value,
				isShown: false,
			};
			setIsClosing(false);
		}, 350);
	};

	const handleInputBlur = (): void => {
		setFocusedInputId(null);
	};

	const handleInputFocus = (event: FocusEvent<HTMLInputElement>): void => {
		setFocusedInputId(event.currentTarget.id);
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
		event.preventDefault();
		void submitShortcut();
	};

	const setIcon = (iconOption: IconOption, url: string): void => {
		signals.editingShortcut.value = {
			...signals.editingShortcut.value,
			icon: url,
		};
		setSelectedIconOption(iconOption);
		setCurrentPage(EditShortcutPopupPage.MAIN);
	};

	const submitShortcut = async (): Promise<void> => {
		const shortcut = { ...signals.editingShortcut.value };
		if (!shortcut.url) {
			return;
		}
		handleClose();
		signals.isLoadingShown.value = true;
		try {
			if (!shortcut.title || !shortcut.icon) {
				const siteInfo = await api.getSiteInfo(shortcut.url);
				if (!shortcut.title) {
					shortcut.title = siteInfo.title;
				}
				if (!shortcut.icon) {
					if (siteInfo.icon) {
						shortcut.icon = siteInfo.icon;
					} else {
						shortcut.icon = getColorUrl();
					}
				}
			}
			const queryDataManager = new QueryDataManager(queryClient);
			if (shortcut.id) {
				await queryDataManager.editBookmark(
					shortcut,
					signals.currentFolder.value?.id,
					selectedFolder?.id,
				);
			} else {
				await queryDataManager.createBookmark(
					shortcut,
					selectedFolder?.id,
				);
			}
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	useSignalEffect(() => {
		setSelectedFolder(signals.currentFolder.value);
	});

	return (
		<MenuShell
			className={styles["edit-shortcut-popup"]}
			isClosingMenu={isClosing}
			isModal={true}
			isTransformOriginDeclared={true}
			onClose={handleClose}
			shouldUseAlternativeOverflowPosition={true}
			show={signals.editShortcutPopup.value.isShown}
			x={signals.editShortcutPopup.value.position.left}
			y={signals.editShortcutPopup.value.position.top}
		>
			<form
				className={clsx(
					currentPage !== EditShortcutPopupPage.MAIN &&
						styles["invisible"],
					currentPage !== EditShortcutPopupPage.MAIN &&
						styles["slid"],
				)}
				onSubmit={handleSubmit}
			>
				<div className={styles["title-bar"]}>
					{signals.editingShortcut.value.id
						? t("editShortcut")
						: t("addShortcut")}
					<PopupCloseButton onClick={handleClose} />
				</div>
				<div
					className={clsx(
						"input",
						styles["input-wrapper"],
						focusedInputId === urlInputId && styles["focused"],
					)}
				>
					<label htmlFor={urlInputId}>
						<FontAwesomeIcon
							icon={faLink}
							fixedWidth
						/>
						<span hidden>{t("url")}</span>
					</label>
					<input
						autoComplete="off"
						id={urlInputId}
						inputMode="url"
						onBlur={handleInputBlur}
						onFocus={handleInputFocus}
						placeholder={t("url")}
						required={true}
						value={signals.editingShortcut.value.url || ""}
						{...urlInput}
					/>
				</div>
				<div
					className={clsx(
						"input",
						styles["input-wrapper"],
						focusedInputId === titleInputId && styles["focused"],
					)}
				>
					<label htmlFor={titleInputId}>
						<FontAwesomeIcon
							icon={faHeading}
							fixedWidth
						/>
						<span hidden>{t("title")}</span>
					</label>
					<input
						autoComplete="off"
						id={titleInputId}
						onBlur={handleInputBlur}
						onFocus={handleInputFocus}
						placeholder={t("titleLeaveBlankToAutoObtain")}
						value={signals.editingShortcut.value.title}
						{...titleInput}
					/>
				</div>
				<button
					className={styles["detail-button"]}
					onClick={(): void => {
						setCurrentPage(EditShortcutPopupPage.ICON);
					}}
					type="button"
				>
					<div>
						<FontAwesomeIcon
							icon={faIcons}
							fixedWidth
						/>
						{t("icon")}
					</div>
					<div className={styles["detail-button-value"]}>
						<span>
							{
								{
									[IconOption.AUTO]: t("auto"),
									[IconOption.WEBSITE_DEFAULT]:
										t("websiteDefault"),
									[IconOption.LIME_GENERATED]:
										t("generatedByLime"),
									[IconOption.URL]: t("specifiedUrl"),
								}[calculatedIconOption]
							}
						</span>
						<FontAwesomeIcon
							fixedWidth
							icon={faAngleRight}
						/>
					</div>
				</button>
				<button
					className={styles["detail-button"]}
					onClick={(): void => {
						setCurrentPage(EditShortcutPopupPage.FOLDER);
					}}
					type="button"
				>
					<div>
						<FontAwesomeIcon
							fixedWidth
							icon={faFolderClosed}
						/>
						{t("folder")}
					</div>
					<div className={styles["detail-button-value"]}>
						<span>{selectedFolder?.folderName || t("none")}</span>
						<FontAwesomeIcon
							fixedWidth
							icon={faAngleRight}
						/>
					</div>
				</button>
				<button
					className={styles["submit-button"]}
					type="submit"
				>
					{signals.editingShortcut.value.id ? t("save") : t("add")}
				</button>
			</form>
			<EditShortcutIconPage
				isShown={currentPage === EditShortcutPopupPage.ICON}
				selectedIconOption={calculatedIconOption}
				setCurrentPage={setCurrentPage}
				setIcon={setIcon}
			/>
			<EditShortcutFolderPage
				isShown={currentPage === EditShortcutPopupPage.FOLDER}
				selectedFolder={selectedFolder}
				setCurrentPage={setCurrentPage}
				setSelectedFolder={setSelectedFolder}
			/>
		</MenuShell>
	);
}

export default EditShortcutPopup;
